import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import {
  ArrowLeft,
  FileSpreadsheet,
  Loader2,
  Sparkles,
  Upload,
  CheckCircle,
  AlertCircle,
  Download,
} from 'lucide-react';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';

type ImportFieldKey =
  | 'name'
  | 'email'
  | 'phone'
  | 'profileImageUrl'
  | 'notes'
  | 'instagramHandle'
  | 'instagramHyperlink'
  | 'instagramProfileUrl'
  | 'tiktokHandle'
  | 'tiktokHyperlink'
  | 'tiktokProfileUrl'
  | 'youtubeChannel'
  | 'youtubeHyperlink'
  | 'youtubeChannelUrl'
  | 'facebookHandle'
  | 'facebookHyperlink'
  | 'facebookProfileUrl'
  | 'wechatId'
  | 'wechatProfileUrl'
  | 'whatsappNumber'
  | 'bio'
  | 'locationCity'
  | 'ethnicity'
  | 'avgPostRate'
  | 'avgVideoRate'
  | 'avgStoryRate'
  | 'bankDetails'
  | 'icPassport'
  | 'contentSpecialties'
  | 'languages'
  | 'tags'
  | 'gender'
  | 'instagramFollowers'
  | 'tiktokFollowers'
  | 'youtubeFollowers'
  | 'facebookFollowers'
  | 'wechatFollowers';

const FIELD_OPTIONS: { value: ImportFieldKey | 'ignore'; label: string }[] = [
  { value: 'ignore', label: '— Ignore —' },
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'profileImageUrl', label: 'Profile image URL' },
  { value: 'notes', label: 'Notes' },
  { value: 'instagramHandle', label: 'Instagram (username or URL)' },
  {
    value: 'instagramHyperlink',
    label: 'Instagram (HYPERLINK: name + URL from one column)',
  },
  { value: 'instagramProfileUrl', label: 'Instagram profile URL (link column)' },
  { value: 'tiktokHandle', label: 'TikTok username / @handle (or URL in cell)' },
  {
    value: 'tiktokHyperlink',
    label: 'TikTok (HYPERLINK: display text + profile URL in one column)',
  },
  { value: 'tiktokProfileUrl', label: 'TikTok profile URL (link column)' },
  { value: 'youtubeChannel', label: 'YouTube (channel name, @handle, or URL in cell)' },
  {
    value: 'youtubeHyperlink',
    label: 'YouTube (HYPERLINK: display text + channel URL in one column)',
  },
  { value: 'youtubeChannelUrl', label: 'YouTube channel / profile URL (link column)' },
  { value: 'facebookHandle', label: 'Facebook (username, page name, or URL in cell)' },
  {
    value: 'facebookHyperlink',
    label: 'Facebook (HYPERLINK: display text + page URL in one column)',
  },
  { value: 'facebookProfileUrl', label: 'Facebook profile / page URL (link column)' },
  { value: 'wechatId', label: 'WeChat ID' },
  { value: 'wechatProfileUrl', label: 'WeChat link / profile URL (optional)' },
  { value: 'whatsappNumber', label: 'WhatsApp' },
  { value: 'bio', label: 'Bio' },
  { value: 'locationCity', label: 'Location / city' },
  { value: 'ethnicity', label: 'Ethnicity' },
  { value: 'avgPostRate', label: 'Avg post rate (RM)' },
  { value: 'avgVideoRate', label: 'Avg video rate (RM)' },
  { value: 'avgStoryRate', label: 'Avg story rate (RM)' },
  { value: 'bankDetails', label: 'Bank details' },
  { value: 'icPassport', label: 'IC / Passport' },
  { value: 'contentSpecialties', label: 'Content specialties (list)' },
  { value: 'languages', label: 'Languages (list)' },
  { value: 'tags', label: 'Tags (names; semicolon-separated)' },
  { value: 'gender', label: 'Gender (FEMALE, MALE, …)' },
  { value: 'instagramFollowers', label: 'Instagram followers (count)' },
  { value: 'tiktokFollowers', label: 'TikTok followers (count)' },
  { value: 'youtubeFollowers', label: 'YouTube subscribers (count)' },
  { value: 'facebookFollowers', label: 'Facebook followers (count)' },
  { value: 'wechatFollowers', label: 'WeChat followers (count)' },
];

interface PreviewSheet {
  sheetName: string;
  headers: string[];
  suggestedMap: Record<string, { field: string; score: number }>;
  previewRows: Array<Record<string, { text: string; href?: string }>>;
  totalRows: number;
}

function buildColumnMap(
  headers: string[],
  headerToField: Record<string, ImportFieldKey | 'ignore'>
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const h of headers) {
    const f = headerToField[h];
    if (f && f !== 'ignore') map[f] = h;
  }
  return map;
}

function cellDisplay(c: { text: string; href?: string }): string {
  if (c.href) return c.text ? `${c.text} (${c.href})` : c.href;
  return c.text || '';
}

export default function KOLBulkImport() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const agencyOrgId = useMemo(() => {
    const m = user?.memberships?.find((x) => x.organization?.type === 'AGENCY');
    return m?.organizationId;
  }, [user?.memberships]);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewSheet[] | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [activeSheet, setActiveSheet] = useState(0);
  const [includeSheet, setIncludeSheet] = useState<Record<string, boolean>>({});
  const [headerToField, setHeaderToField] = useState<Record<string, Record<string, ImportFieldKey | 'ignore'>>>(
    {}
  );
  const [aiBusy, setAiBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<{
    created: number;
    total: number;
    mergedSamePerson: number;
    errors: {
      row: number;
      error?: string;
      sheet?: string;
      rowSnapshot?: Record<string, string>;
    }[];
  } | null>(null);

  const initMappingsFromPreview = useCallback((sheets: PreviewSheet[]) => {
    const next: Record<string, Record<string, ImportFieldKey | 'ignore'>> = {};
    const inc: Record<string, boolean> = {};
    for (const s of sheets) {
      inc[s.sheetName] = s.totalRows > 0;
      const m: Record<string, ImportFieldKey | 'ignore'> = {};
      for (const h of s.headers) {
        const sug = s.suggestedMap[h];
        const f = (sug?.field as ImportFieldKey | 'ignore') || 'ignore';
        m[h] = f === 'ignore' ? 'ignore' : f;
      }
      next[s.sheetName] = m;
    }
    setIncludeSheet(inc);
    setHeaderToField(next);
  }, []);

  const runPreview = async (f: File) => {
    setLoadingPreview(true);
    setImportSummary(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post<{ sheets: PreviewSheet[] }>('/kols/bulk-import/preview-excel', fd);
      setPreview(res.data.sheets);
      initMappingsFromPreview(res.data.sheets);
      setActiveSheet(0);
      setStep(2);
    } catch (e: unknown) {
      console.error(e);
      alert((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  const onDropFile = (f: File | null) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      alert('Please choose an .xlsx file');
      return;
    }
    setFile(f);
    void runPreview(f);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
    disabled: step !== 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) onDropFile(acceptedFiles[0]);
    },
    noKeyboard: true,
  });

  const currentSheet = preview?.[activeSheet];

  const applyAiSuggestions = async () => {
    if (!currentSheet) return;
    setAiBusy(true);
    try {
      const sampleRows = currentSheet.previewRows.map((row) =>
        Object.fromEntries(Object.entries(row).map(([k, v]) => [k, cellDisplay(v)]))
      );
      const res = await api.post<{
        source: string;
        suggested: Record<string, { field: string; score: number }>;
      }>('/kols/bulk-import/suggest-mapping', {
        sheetName: currentSheet.sheetName,
        headers: currentSheet.headers,
        sampleRows,
      });
      const sug = res.data.suggested;
      setHeaderToField((prev) => {
        const copy = { ...prev };
        const m = { ...copy[currentSheet.sheetName] };
        for (const h of currentSheet.headers) {
          const s = sug[h];
          if (s?.field && s.field !== 'ignore') {
            m[h] = s.field as ImportFieldKey;
          }
        }
        copy[currentSheet.sheetName] = m;
        return copy;
      });
    } catch (e) {
      console.error(e);
      alert('AI suggestion failed');
    } finally {
      setAiBusy(false);
    }
  };

  const runImport = async () => {
    if (!file || !agencyOrgId || !preview?.length) return;
    setImporting(true);
    setImportSummary(null);
    try {
      const sheetsPayload = preview.map((s) => ({
        sheetName: s.sheetName,
        include: includeSheet[s.sheetName] !== false,
        columnMap: buildColumnMap(s.headers, headerToField[s.sheetName] || {}),
      }));

      const fd = new FormData();
      fd.append('file', file);
      fd.append(
        'payload',
        JSON.stringify({
          agencyOrgId,
          sheets: sheetsPayload,
        })
      );

      const res = await api.post<{
        total: number;
        created: number;
        results: {
          row: number;
          ok: boolean;
          error?: string;
          sheet?: string;
          rowSnapshot?: Record<string, string>;
          skipped?: boolean;
        }[];
      }>('/kols/bulk-import/excel', fd);

      const all = res.data.results || [];
      const errors = all
        .filter((r) => !r.ok)
        .map((r) => ({
          row: r.row,
          error: r.error,
          sheet: r.sheet,
          rowSnapshot: r.rowSnapshot,
        }));
      const mergedSamePerson = all.filter((r) => r.ok && r.skipped).length;
      setImportSummary({
        created: res.data.created,
        total: res.data.total,
        mergedSamePerson,
        errors,
      });
      void queryClient.invalidateQueries({ queryKey: ['agency', 'kols'] });
      setStep(3);
    } catch (e: unknown) {
      console.error(e);
      alert((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadFailedRowsXlsx = useCallback(() => {
    if (!importSummary?.errors?.length) return;
    const withSnap = importSummary.errors.filter(
      (e) => e.rowSnapshot && Object.keys(e.rowSnapshot).length > 0
    );
    if (!withSnap.length) {
      window.alert('No cell data to export. Run the import again with the latest app—failed rows should include a snapshot.');
      return;
    }
    const headerSet = new Set<string>();
    for (const e of withSnap) {
      Object.keys(e.rowSnapshot || {}).forEach((k) => headerSet.add(k));
    }
    const dataCols = [...headerSet].sort((a, b) => a.localeCompare(b));
    const meta = ['import_sheet', 'import_row', 'import_error'] as const;
    const head = [...meta, ...dataCols];
    const aoa: (string | number)[][] = [head];
    for (const e of withSnap) {
      const snap = e.rowSnapshot || {};
      aoa.push([
        e.sheet || '',
        e.row,
        e.error || '',
        ...dataCols.map((h) => snap[h] ?? ''),
      ]);
    }
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    XLSX.utils.book_append_sheet(wb, ws, 'Failed rows');
    const safe = new Date().toISOString().replace(/[:]/g, '-').slice(0, 19);
    XLSX.writeFile(wb, `kol-bulk-import-failed-${safe}.xlsx`);
  }, [importSummary]);

  if (!agencyOrgId) {
    return (
      <div className="p-6">
        <p className="text-glass-text-2">No agency organization found.</p>
        <Link to="/agency/kols" className="text-accent2 hover:underline">
          Back to KOLs
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/agency/kols')}
          className="p-2 rounded-lg hover:bg-glass-3 text-glass-text-2"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="page-title flex items-center gap-2">
            <FileSpreadsheet className="w-7 h-7 text-accent2" />
            Bulk import KOLs (Excel)
          </h1>
          <p className="text-glass-text-2 text-sm mt-1">
            Upload an .xlsx file, confirm column mapping per sheet, then import. HYPERLINK
            formulas are parsed server-side.
          </p>
        </div>
      </div>

      <div className="flex gap-2 text-sm">
        {[1, 2, 3].map((n) => (
          <Badge
            key={n}
            variant={step >= n ? 'default' : 'secondary'}
            className={step === n ? 'bg-accent2/20 text-accent2 border-accent2/30' : ''}
          >
            {n === 1 && '1. Upload'}
            {n === 2 && '2. Map columns'}
            {n === 3 && '3. Results'}
          </Badge>
        ))}
      </div>

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Upload workbook</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              {...getRootProps()}
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-12 cursor-pointer transition-colors outline-none ${
                isDragActive
                  ? 'border-accent2 bg-accent2/10'
                  : 'border-glass-strong hover:border-accent2/60 hover:bg-glass-2'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-10 h-10 text-accent2 mb-2" />
              <span className="text-glass-text text-center">
                {isDragActive
                  ? 'Drop the .xlsx file here'
                  : 'Drop .xlsx here or click to browse'}
              </span>
              <span className="text-xs text-glass-text-3 mt-2">Excel .xlsx only</span>
            </div>
            {loadingPreview && (
              <div className="flex items-center gap-2 text-accent2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Parsing sheets…
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {step === 2 && preview && currentSheet && (
        <>
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
              <div>
                <CardTitle>Map columns</CardTitle>
                {file && (
                  <p className="text-xs text-glass-text-3 mt-1 font-mono truncate max-w-md">
                    {file.name}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setStep(1)}>
                  New file
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void applyAiSuggestions()}
                  disabled={aiBusy}
                >
                  {aiBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  AI suggest (current sheet)
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2 border-b border-glass pb-3">
                {preview.map((s, i) => (
                  <button
                    key={s.sheetName}
                    type="button"
                    onClick={() => setActiveSheet(i)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      activeSheet === i
                        ? 'bg-accent2/20 text-accent2 border-accent2/30'
                        : 'bg-glass-2 text-glass-text-2 border-glass hover:bg-glass-3'
                    }`}
                  >
                    {s.sheetName}
                    <span
                      className={
                        activeSheet === i ? 'text-accent2/70 ml-1' : 'text-glass-text-3 ml-1'
                      }
                    >
                      ({s.totalRows})
                    </span>
                  </button>
                ))}
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer text-glass-text">
                <input
                  type="checkbox"
                  className="rounded border-glass accent-accent2"
                  checked={includeSheet[currentSheet.sheetName] !== false}
                  onChange={(e) =>
                    setIncludeSheet((prev) => ({
                      ...prev,
                      [currentSheet.sheetName]: e.target.checked,
                    }))
                  }
                />
                Include this sheet in import
              </label>

              <div className="overflow-x-auto rounded-xl border border-glass">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="bg-glass-3 text-glass-text-2 uppercase tracking-[0.05em] text-xs">
                      <th className="p-3 font-medium">Excel column</th>
                      <th className="p-3 font-medium w-64">Import as</th>
                      <th className="p-3 font-medium">Hint</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSheet.headers.map((h) => {
                      const val = headerToField[currentSheet.sheetName]?.[h] ?? 'ignore';
                      const hint = currentSheet.suggestedMap[h];
                      return (
                        <tr key={h} className="border-t border-glass">
                          <td className="p-3 align-top font-mono text-xs text-glass-text">
                            {h}
                          </td>
                          <td className="p-3 align-top">
                            <select
                              className="glass-input w-full px-2 py-1.5 text-sm"
                              value={val}
                              onChange={(e) => {
                                const v = e.target.value as ImportFieldKey | 'ignore';
                                setHeaderToField((prev) => ({
                                  ...prev,
                                  [currentSheet.sheetName]: {
                                    ...prev[currentSheet.sheetName],
                                    [h]: v,
                                  },
                                }));
                              }}
                            >
                              {FIELD_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-3 align-top text-xs text-glass-text-3">
                            {hint && hint.field !== 'ignore' ? (
                              <>
                                auto: {hint.field}{' '}
                                <span className="text-glass-text-3">
                                  ({hint.score.toFixed(2)})
                                </span>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div>
                <h3 className="text-sm font-medium text-glass-text mb-2">
                  Preview (first rows)
                </h3>
                <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-xl border border-glass">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-glass-3 backdrop-blur-2xl">
                      <tr>
                        {currentSheet.headers.map((h) => (
                          <th
                            key={h}
                            className="p-2 text-left font-medium border-b border-glass text-glass-text-2 whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentSheet.previewRows.map((row, ri) => (
                        <tr key={ri} className="border-b border-glass">
                          {currentSheet.headers.map((h) => (
                            <td
                              key={h}
                              className="p-2 text-glass-text max-w-[200px] truncate"
                              title={cellDisplay(row[h] || { text: '' })}
                            >
                              {cellDisplay(row[h] || { text: '' })}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button type="button" onClick={() => void runImport()} disabled={importing}>
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Importing…
                    </>
                  ) : (
                    'Run import'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {step === 3 && importSummary && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-500 dark:text-emerald-300" />
              Import finished
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-glass-text">
              Created{' '}
              <strong className="text-glass-text">{importSummary.created}</strong> of{' '}
              <strong className="text-glass-text">{importSummary.total}</strong> rows
              {importSummary.mergedSamePerson > 0 ? (
                <>
                  {' '}
                  (plus{' '}
                  <strong className="text-glass-text">{importSummary.mergedSamePerson}</strong> recognised as
                  existing KOLs — same Instagram and matching email, no duplicate row)
                </>
              ) : null}
              .
            </p>
            {importSummary.errors.length > 0 && (
              <div>
                <p className="text-amber-500 dark:text-amber-300 flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4" />
                  {importSummary.errors.length} row(s) skipped or failed
                </p>
                <div className="max-h-48 overflow-y-auto rounded-xl border border-glass text-sm font-mono bg-glass-2">
                  {importSummary.errors.slice(0, 50).map((e, i) => (
                    <div
                      key={i}
                      className="px-2 py-1 border-b border-glass text-glass-text"
                    >
                      {e.sheet && <span className="text-accent2">{e.sheet} </span>}
                      row {e.row}: {e.error}
                    </div>
                  ))}
                  {importSummary.errors.length > 50 && (
                    <div className="p-2 text-glass-text-3">
                      …and {importSummary.errors.length - 50} more
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {importSummary.errors.length > 0 && (
                <Button type="button" variant="secondary" onClick={downloadFailedRowsXlsx}>
                  <Download className="w-4 h-4 mr-2" />
                  Download failed rows (.xlsx)
                </Button>
              )}
              <Button type="button" onClick={() => navigate('/agency/kols')}>
                View KOLs
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setStep(1);
                  setFile(null);
                  setPreview(null);
                  setImportSummary(null);
                }}
              >
                Import another file
              </Button>
            </div>
            <p className="text-xs text-glass-text-3 max-w-2xl">
              Multiple addresses in the Email field can be separated by commas, semicolons, or pipes; the first is stored
              as the main KOL email, the rest as additional contacts. Outbound email notifications go to every address
              on file. The main address can be changed in the KOL record; you can set additional addresses via API field{' '}
              <code className="text-glass-text">additionalEmails</code> (array) on update.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
